import { useMemo } from 'react';

import { useMapFields, type MapField } from '@/features/fields';

import { useMapDevices } from './map-hooks';

/**
 * The saved Field a device is linked to — name, crop, crop year, place and acreage.
 *
 * **Why this is a join and not a field on the details payload.** Probed live 2026-09-01 (and
 * recorded in `greenway_mobile/lib/features/devices/data/device_field_links.dart`, which does
 * exactly this):
 *
 * | Endpoint | carries the link? |
 * | --- | --- |
 * | `GET /devices/tracking/{id}` | `location` is only `{latitude, longitude}` — no field, crop, place or acreage |
 * | `GET /devices/tracking/list` | `field: null`, for every row |
 * | `GET /devices/tracking/map`  | `fieldId` — the only route that carries it |
 * | `GET /fields/tracking`       | the Field itself: name, crop, `cropYear`, acreage, city, state, boundary |
 *
 * So Device Details read its field name, crop chip, crop year and acreage off a payload that never
 * contained them, and rendered nothing on real data however well the card was styled.
 *
 * Both queries are ones the Map tab already owns, so on a warm cache this join costs no request.
 *
 * **The association is the map route's `fieldId` and nothing else — there is deliberately no
 * geometric fallback.** An absent `fieldId` is not missing data; it is the server saying *not
 * linked*, and `CONTEXT.md` reserves that verdict to the server ("the client may pre-empt an
 * obvious refusal but never overrules one"). A device with a location inside no linked Boundary is
 * an **unassociated device**, a real state, and it renders fieldless here exactly as the Map's
 * quick view — which reads the same server association — renders it. An earlier draft hit-tested
 * containment client-side; besides overruling the server, it could not agree with the Map by
 * construction, because it asked the *exclusive*-edge question (`isStrictlyInsideAnyField`, which
 * ADR-0018 scopes to the Field Mapping pre-filter) while the Map resolves with `resolveFieldAt` —
 * inclusive, edge-tolerant, `mostRecent` tie-break.
 *
 * Resolves to `null` rather than throwing on failure. This is decoration on a page whose primary
 * payload has already loaded — a failed fields or map read must degrade to "no field shown", never
 * to an error over a device that otherwise rendered fine.
 */
export function useDeviceFieldLinks(): {
  links: ReadonlyMap<string, MapField>;
  fields: readonly MapField[];
  isLoading: boolean;
} {
  const pins = useMapDevices();
  const fields = useMapFields();

  // Both responses are `{ data, meta }` — `data` is the row array.
  const pinRows = pins.data?.data;
  const fieldRows = fields.data?.data;

  const links = useMemo<ReadonlyMap<string, MapField>>(() => {
    const byId = new Map<string, MapField>();
    if (!fieldRows?.length || !pinRows?.length) return byId;

    const fieldsById = new Map(fieldRows.map((row) => [row.id, row] as const));
    for (const pin of pinRows) {
      const linked = pin.fieldId ? fieldsById.get(pin.fieldId) : undefined;
      if (linked) byId.set(pin.id, linked);
    }
    return byId;
  }, [fieldRows, pinRows]);

  return { links, fields: fieldRows ?? [], isLoading: pins.isLoading || fields.isLoading };
}

/**
 * One device's linked Field, or `null` when the server links it to none.
 *
 * `null` covers both an unassociated device and a device the map route does not list at all; both
 * render fieldless, which is what the server is reporting in either case.
 */
export function useDeviceLinkedField(deviceId: string | null): {
  field: MapField | null;
  isLoading: boolean;
} {
  const { links, isLoading } = useDeviceFieldLinks();

  const field = useMemo<MapField | null>(
    () => (deviceId ? (links.get(deviceId) ?? null) : null),
    [deviceId, links],
  );

  return { field, isLoading };
}
